import { create } from 'zustand';

interface CheckoutActivityState {
  /** How many checkout surfaces are currently live. */
  holds: number;
  acquireCheckoutHold: () => void;
  releaseCheckoutHold: () => void;
  reset: () => void;
}

/**
 * Tracks whether the customer is part-way through a booking, so the session watchdog can
 * defer signing them out (PB-5476, AC 6).
 *
 * ## Invariants:
 * - A COUNTER, not a boolean. Why: two checkout surfaces can be live at once — the reserve
 *   payment page with a payment-method sheet open over it, for example. A boolean would be
 *   cleared by whichever unmounts first, dropping the guard while the other is still
 *   collecting card details.
 * - Never negative. A stray extra release must not push the count below zero, or the next
 *   acquire would leave it at zero and the guard would silently do nothing.
 * - Holds suppress the IDLE deadline only; the absolute cap is enforced regardless. That rule
 *   lives in `useSessionTimeout`, not here — this store only reports whether a checkout is
 *   live. A cap on total session length that any page could suspend would not be a cap.
 */
export const useCheckoutActivityStore = create<CheckoutActivityState>((set) => ({
  holds: 0,
  acquireCheckoutHold: () => set((state) => ({ holds: state.holds + 1 })),
  releaseCheckoutHold: () => set((state) => ({ holds: Math.max(0, state.holds - 1) })),
  reset: () => set({ holds: 0 }),
}));

/**
 * Read outside React — the session tick calls this every second and must not re-subscribe
 * or re-render when the count changes.
 */
export const isCheckoutInProgress = (): boolean => useCheckoutActivityStore.getState().holds > 0;
